import RecruitmentAsset from "./RecruitmentAsset";
import staticQuestionsIllustration from "../assets/ScheduleInterviewAssets/questionsimg.svg";
import "./RecruitmentForms.css";
//INTERVIEW FORM
import React, { useState, useEffect, useCallback } from "react";
import { authFetch } from "../services/httpClient";
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Grid,
  Card,
  CardContent,
  Avatar,
  LinearProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  FormHelperText,
  Alert,
  Collapse,
  InputAdornment,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import {
  UploadFile as UploadFileIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Work as WorkIcon,
  Folder as FolderIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  Close as CloseIcon,
  Warning as AlertIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Code as CodeIcon,
  Quiz as QuizIcon,
} from "@mui/icons-material";
import { styled, useTheme } from "@mui/material/styles";
import { Link as RouterLink } from "react-router-dom"; // Added import for RouterLink
import TechStackModal from "./TechStackModal";
import { useNotification } from "../../interviewAI/NotificationContext";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { getTimeZones } from "@vvo/tzdb";
import { ListSubheader } from "@mui/material";
import {
  getTemplates,
  getTemplate,
  getInterview,
  scheduleInterview,
  uploadCandidatePhoto,
  updateInterview,
  getCodeEvaluationLanguages,
  // calculateATSScore,
} from "../services/api";
import { getSystemServiceSettings, getAvailableInterviewLanguages, getCurrentLanguage, getVideoProctoringConfig } from "../services/settingsapi";

const difficultyTagStyle = (difficulty) => {
  const value = String(difficulty || "").toLowerCase();
  if (value === "advanced" || value === "high") return { backgroundColor: "#FFF0F0", color: "#D32F2F", border: "1px solid #D32F2F" };
  if (value === "intermediate" || value === "medium") return { backgroundColor: "#FFF3D8", color: "#A86600", border: "1px solid #A86600" };
  return { backgroundColor: "#E2F8EC", color: "#008E53", border: "1px solid #008E53" };
};

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const StyledCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  borderRadius: "12px",
  boxShadow: "var(--shadow)",
  backgroundColor: "var(--bg-card)",
  border: "1px solid var(--border-color)",
  transition: "box-shadow 0.2s ease-in-out",
  "&:hover": {
    boxShadow: "0 6px 16px rgba(0,0,0,0.1)",
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  paddingBottom: theme.spacing(1),
  borderBottom: `2px solid var(--primary)`,
  display: "inline-block",
  color: "var(--text-primary)",
  fontWeight: 800,
}));

const UploadButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(1),
  padding: theme.spacing(1, 2),
  border: `2px dashed var(--border-color)`,
  backgroundColor: "var(--bg-input)",
  color: "var(--text-primary)",
  "&:hover": {
    backgroundColor: "var(--primary-light)",
    border: `2px dashed var(--primary)`,
  },
}));

const InterviewForm = () => {
  const theme = useTheme();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);
  const isFromJobOpenings = searchParams.get("from") === "job-openings";
  const backButtonLabel = isFromJobOpenings ? "Back to Job Openings" : "Back to Interviews";
  const backButtonPath = isFromJobOpenings ? "/templates" : "/interviews";

  // Declare all useState hooks BEFORE any conditional returns
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplatesId] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedReqId, setSelectedReqId] = useState(""); // for Req ID selector
  const [techStackWithPercentages, setTechStackWithPercentages] = useState([]);
  const [techStackDifficulty, setTechStackDifficulty] = useState([]);
  const [openTechEditor, setOpenTechEditor] = useState(false);
  const [cvFile, setCvFile] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  // Add this line with other state declarations
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [timezoneError, setTimezoneError] = useState(false); // State for timezone error
  const [allTimezones, setAllTimezones] = useState({
    yourTimezone: null,
    commonTimezones: [],
    otherTimezones: [],
  }); const [supportedLanguages, setSupportedLanguages] = useState([]);
  const [supportedLanguagesLoading, setSupportedLanguagesLoading] = useState(false);
  const [videoProctoringEnabled, setVideoProctoringEnabled] = useState(false);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [successDialogMessage, setSuccessDialogMessage] = useState("");
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorDialogMessage, setErrorDialogMessage] = useState("");

  const [formData, setFormData] = useState({
    templateId: "",
    candidateName: "",
    candidateEmail: "",
    interviewDate: "",
    interviewTime: "",
    interviewTimezone: "",
    interviewDuration: "",
    techStackData: "",
    cvFileName: "",
    imageFileName: "",
    candidateExperience: "",
    staticQuestionsEnabled: false,
    staticQuestions: [],
    codeEvaluation: false,
    codingLanguages: [], // Array of { language, complexity }
    secondCameraEnabled: false,
    systemCheckEnabled: false,
    codeEvaluationEnabled: false,
    resumeEvaluationEnabled: false,
    resumeEvaluation: false,
    resumeEvaluationTimePercentage: 0,
    preferredLanguage: "English",
  });
  const isPlannedReadyInterviewLocked =
    Boolean(selectedTemplate?.plannedInterview) &&
    String(selectedTemplate?.questionBankStatus || "").toUpperCase() === "READY";

  const [experienceLoading, setExperienceLoading] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [imageUploadResponse, setImageUploadResponse] = useState(null);

  const [openStaticDialog, setOpenStaticDialog] = useState(false);
  const [newQuestion, setNewQuestion] = useState("");
  const [languages, setLanguages] = useState([]);
  const [languagesLoading, setLanguagesLoading] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState("");
  const [selectedComplexity, setSelectedComplexity] = useState("Intermediate");

  const formatTimezoneLabel = (timezone) => {
    const city =
      timezone.mainCities?.[0] ||
      timezone.name.split("/").pop().replace(/_/g, " ");

    const offset = timezone.currentTimeOffsetInMinutes;

    const sign = offset >= 0 ? "+" : "-";
    const hours = String(Math.floor(Math.abs(offset) / 60)).padStart(2, "0");
    const minutes = String(Math.abs(offset) % 60).padStart(2, "0");

    const gmt = `GMT${sign}${hours}:${minutes}`;

    return `${timezone.countryName} • ${city} (${gmt}) (${timezone.abbreviation})`;
  };

  const buildTimezoneOption = (timezone) => ({
    value: timezone.name,
    label: formatTimezoneLabel(timezone),
    country: timezone.countryName,
    city:
      timezone.mainCities?.[0] ||
      timezone.name.split("/").pop().replace(/_/g, " "),
    abbreviation: timezone.abbreviation,
  });
  // Fetch all timezones on component mount
  // useEffect(() => {
  //   const fetchTimezones = async () => {
  //     try {
  //       const rawTimezones = Intl.supportedValuesOf("timeZone");

  //       // Helper function to get offset in minutes for a specific timezone at a specific date
  //       // Using a fixed date (e.g., January 1st, 2024) to represent standard time offset
  //       // Or use current date for the current offset
  //       const getOffsetForTimezone = (tz, date = new Date()) => {
  //         const utcDate = new Date(
  //           date.toLocaleString("en-US", { timeZone: "UTC" })
  //         );
  //         const tzDate = new Date(
  //           date.toLocaleString("en-US", { timeZone: tz })
  //         );
  //         return (tzDate.getTime() - utcDate.getTime()) / (1000 * 60);
  //       };

  //       const formattedTimezones = rawTimezones
  //         .map((tz) => {
  //           const offsetMinutes = getOffsetForTimezone(tz); // Calculate offset for current date
  //           const offsetHours = Math.floor(Math.abs(offsetMinutes) / 60);
  //           const offsetMinutesPart = Math.abs(offsetMinutes) % 60;
  //           const sign = offsetMinutes >= 0 ? "+" : "-";
  //           const formattedOffset = `(GMT${sign}${String(offsetHours).padStart(
  //             2,
  //             "0"
  //           )}:${String(offsetMinutesPart).padStart(2, "0")})`;
  //           return { value: tz, label: `${formattedOffset} ${tz}` };
  //         })
  //         .sort((a, b) => a.label.localeCompare(b.label)); // Sort alphabetically by label

  //       setAllTimezones(formattedTimezones);

  //       const systemTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  //       if (!isEditMode) {
  //         setFormData(prev => ({
  //           ...prev,
  //           interviewTimezone: systemTimezone
  //         }));
  //       }

  //       // Optionally, set a default timezone if needed, e.g., user's browser timezone
  //       // setFormData(prev => ({ ...prev, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone }));
  //     } catch (error) {
  //       console.error("Error fetching timezones:", error);
  //       // Fallback to an empty array or a default list if needed
  //       setAllTimezones([]);
  //     }
  //   };

  //   fetchTimezones();
  // }, [isEditMode]); // Empty dependency array ensures this runs only once on mount
  const tzdbTimezones = getTimeZones();
  useEffect(() => {
    try {
      const browserTimezone =
        Intl.DateTimeFormat().resolvedOptions().timeZone;

      const timezoneAliases = {
        "Asia/Calcutta": "Asia/Kolkata",
      };

      const systemTimezone =
        timezoneAliases[browserTimezone] || browserTimezone;
      console.log("System Timezone:", systemTimezone);

      const detectedTimezone = tzdbTimezones.find(
        (tz) => tz.name === systemTimezone
      );
      console.log("Detected Timezone:", detectedTimezone);

      const yourTimezone = detectedTimezone
        ? buildTimezoneOption(detectedTimezone)
        : null;

      const commonTimezones = detectedTimezone
        ? tzdbTimezones
          .filter(
            (tz) =>
              tz.countryName === detectedTimezone.countryName &&
              tz.name !== detectedTimezone.name
          )
          .map(buildTimezoneOption)
        : [];

      const otherTimezones = tzdbTimezones
        .filter(
          (tz) =>
            tz.name !== detectedTimezone?.name &&
            tz.countryName !== detectedTimezone?.countryName
        )
        .map(buildTimezoneOption)
        .sort((a, b) => a.label.localeCompare(b.label));

      setAllTimezones({
        yourTimezone,
        commonTimezones,
        otherTimezones,
      });

      if (!isEditMode) {

        console.log("Setting formData timezone:", systemTimezone);

        setFormData((prev) => ({
          ...prev,
          interviewTimezone: systemTimezone,
        }));
      }
    } catch (err) {
      console.error("Failed to load timezones", err);

      setAllTimezones({
        yourTimezone: null,
        commonTimezones: [],
        otherTimezones: [],
      });
    }
  }, [isEditMode]);

  const fetchInterviewDetails = useCallback(async () => {
    try {
      const data = await getInterview(id);

      const parseStaticQuestions = (sq) => {
        if (!sq) return [];
        if (Array.isArray(sq)) return sq;
        if (typeof sq === "string") {
          try {
            const parsed = JSON.parse(sq);
            return Array.isArray(parsed) ? parsed : [];
          } catch (e) {
            console.warn("Failed to parse staticQuestions JSON:", e);
            return [];
          }
        }
        return [];
      };

      // Parse coding languages - handle both array format and legacy string format
      let codingLanguages = [];
      if (data.codingLanguage) {
        if (Array.isArray(data.codingLanguage)) {
          codingLanguages = data.codingLanguage;
        } else if (typeof data.codingLanguage === "string") {
          try {
            codingLanguages = JSON.parse(data.codingLanguage);
            if (!Array.isArray(codingLanguages)) codingLanguages = [];
          } catch (e) {
            codingLanguages = [];
          }
        }
      }

      setFormData({
        templateId: data.templateId || "",
        candidateName: data.candidateName || "",
        candidateEmail: data.candidateEmail || "",
        interviewDate: data.interviewDate || "",
        interviewTime: data.interviewTime || "",
        interviewTimezone: data.interviewTimezone || "",
        interviewDuration: data.interviewDuration || "",
        techStackData: "",
        cvFileName: data.cvFileName || "",
        imageFileName: data.imageFileName || "",
        candidateExperience: data.candidateExperience || "",
        staticQuestionsEnabled: data.staticQuestionsEnabled || false,
        staticQuestions: parseStaticQuestions(data.staticQuestions),
        codeEvaluation: data.codeEvaluation || false,
        codingLanguages: codingLanguages,
        secondCameraEnabled: data.secondCameraEnabled || false,
        systemCheckEnabled: data.systemCheckEnabled || false,
        codeEvaluationEnabled: data.codeEvaluationEnabled || false,
        resumeEvaluationEnabled: data.resumeEvaluationEnabled || false,
        resumeEvaluation: data.resumeEvaluation || false,
        resumeEvaluationTimePercentage: data.resumeEvaluationTimePercentage || 0,
        preferredLanguage: data.preferredLanguage || "English",
      });

      if (data.templateId) {
        fetchTemplateDetails(data.templateId);
      }

      if (Array.isArray(data.techStackData)) {
        setTechStackWithPercentages(
          data.techStackData.map((t) => ({
            name: t.name,
            percentage: t.percentage,
          }))
        );
        setTechStackDifficulty(
          data.techStackData.map((t) => ({
            name: t.name,
            difficulty: t.difficulty || "Intermediate",
          }))
        );
      }
    } catch (error) {
      console.error("Error fetching interview:", error);
    }
  }, [id]);

  useEffect(() => {
    if (isEditMode && formData.templateId && templates.length > 0) {
      const matched = templates.find(t => t._id === formData.templateId);
      setSelectedReqId(matched?.reqId || "");
    }
  }, [formData.templateId, templates, isEditMode]);

  useEffect(() => {
    if (!isEditMode && formData.templateId && templates.length > 0) {
      const matched = templates.find(t => t._id === formData.templateId);
      if (matched) {
        setSelectedReqId(matched.reqId || "");
      }
    }
  }, [formData.templateId, templates, isEditMode]);
  useEffect(() => {
    const loadData = async () => {
      // First, always fetch system service settings
      await fetchSystemServiceSettings();

      // Then, if editing an existing interview, fetch its details
      if (id) {
        await fetchInterviewDetails();
      }
    };

    fetchTemplates();
    fetchLanguages();
    fetchSupportedLanguages();
    fetchVideoProctoringConfig();
    loadData();

    // Handle prefill from URL query param (new interview with template)
    const templateIdFromQuery = searchParams.get("templateId");
    if (templateIdFromQuery && !id) {
      setFormData((prev) => ({ ...prev, templateId: templateIdFromQuery }));
      fetchTemplateDetails(templateIdFromQuery);
    }
  }, [id, fetchInterviewDetails]);



  const fetchVideoProctoringConfig = async () => {
    try {
      const data = await getVideoProctoringConfig();
      setVideoProctoringEnabled(data.video_proctoring_enabled);
    } catch (error) {
      console.error("Error fetching video proctoring config:", error);
      // Set default if fetch fails
      setVideoProctoringEnabled(false);
    }
  };

  const fetchSupportedLanguages = async () => {
    setSupportedLanguagesLoading(true);
    try {
      const langData = await getAvailableInterviewLanguages();
      const availableLanguages = (langData.languages || []).filter((language) => language.available);
      setSupportedLanguages(availableLanguages.map((language) => language.language));

      // Set default language from current global setting
      const currentLang = await getCurrentLanguage();
      const configuredLanguage = currentLang.language || "English";
      const englishAvailable = availableLanguages.some((language) => language.language === "English");
      const selectedLanguage = availableLanguages.some((language) => language.language === configuredLanguage)
        ? configuredLanguage
        : englishAvailable
          ? "English"
          : "";
      setFormData((prev) => ({
        ...prev,
        preferredLanguage: selectedLanguage
      }));
    } catch (error) {
      console.error("Error fetching supported languages:", error);
      setSupportedLanguages([]);
    } finally {
      setSupportedLanguagesLoading(false);
    }
  };

  const fetchSystemServiceSettings = async () => {
    try {
      const settings = await getSystemServiceSettings();
      setFormData((prev) => ({
        ...prev,
        secondCameraEnabled: settings.SECOND_CAMERA_ENABLE || false,
        systemCheckEnabled: settings.SYSTEM_CHECK_ENABLE || false,
        codeEvaluationEnabled: settings.CODE_EVALUATION_ENABLE || false,
        resumeEvaluationEnabled: settings.RESUME_EVALUATION_ENABLE || false,
        // Apply settings to the actual toggles - this makes them controlled by system settings
        // just like system check and second camera
        codeEvaluation: prev.codeEvaluation || settings.CODE_EVALUATION_ENABLE || false,
        resumeEvaluation: prev.resumeEvaluation || settings.RESUME_EVALUATION_ENABLE || false,
      }));
    } catch (error) {
      console.error("Error fetching system service settings:", error);
      // Set defaults if fetch fails
      setFormData((prev) => ({
        ...prev,
        secondCameraEnabled: false,
        systemCheckEnabled: false,
        codeEvaluationEnabled: false,
        resumeEvaluationEnabled: false,
      }));
    }
  };

  const fetchLanguages = async () => {
    setLanguagesLoading(true);
    try {
      const langs = await getCodeEvaluationLanguages();
      setLanguages(langs);
    } catch (error) {
      console.error("Error fetching languages:", error);
      setLanguages([]);
    } finally {
      setLanguagesLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await getTemplates();
      setTemplates(response.data || []);
    } catch (error) {
      console.error("Error fetching templates:", error);
      setTemplates([]);
    }
  };

  const fetchTemplateDetails = async (templateId) => {
    try {
      const template = await getTemplate(templateId);
      setSelectedTemplate(template);

      // parse staticQuestions from template (could be array or JSON string)
      const parseStaticFromTemplate = (sq) => {
        if (!sq) return [];
        if (Array.isArray(sq)) return sq;
        if (typeof sq === "string") {
          try {
            const parsed = JSON.parse(sq);
            return Array.isArray(parsed) ? parsed : [];
          } catch (e) {
            return [];
          }
        }
        return [];
      };

      // Parse coding languages - handle both array format and legacy string format
      // Template API returns codingLanguages (plural), Interview API returns codingLanguage (singular)
      let codingLanguages = [];
      const langData = template.codingLanguages || template.codingLanguage;
      if (langData) {
        if (Array.isArray(langData)) {
          codingLanguages = langData;
        } else if (typeof langData === "string") {
          try {
            codingLanguages = JSON.parse(langData);
            if (!Array.isArray(codingLanguages)) codingLanguages = [];
          } catch (e) {
            codingLanguages = [];
          }
        }
      }

      setFormData((prev) => ({
        ...prev,
        interviewDuration: template.interviewDuration || "",
        resumeEvaluation: isEditMode ? prev.resumeEvaluation : template.resumeEvaluation || false,
        resumeEvaluationTimePercentage: isEditMode ? prev.resumeEvaluationTimePercentage : template.resumeEvaluationTimePercentage || 0,
        staticQuestionsEnabled: isEditMode ? prev.staticQuestionsEnabled : template.staticQuestionsEnabled || false,
        staticQuestions: isEditMode ? prev.staticQuestions : parseStaticFromTemplate(template.staticQuestions),
        codeEvaluation: isEditMode ? prev.codeEvaluation : template.codeEvaluation || false,
        codingLanguages: isEditMode ? prev.codingLanguages : codingLanguages,
      }));

      // Only update tech stack from template if NOT in edit mode
      // In edit mode, preserve the interview's tech stack from the API response
      const templateLocksInterviewFields =
        Boolean(template.plannedInterview) &&
        String(template.questionBankStatus || "").toUpperCase() === "READY";

      if (!isEditMode || templateLocksInterviewFields) {
        if (Array.isArray(template.techStackData)) {
          setTechStackWithPercentages(
            template.techStackData.map(({ name, percentage }) => ({
              name,
              percentage,
            }))
          );
          setTechStackDifficulty(
            template.techStackData.map(({ name, difficulty }) => ({
              name,
              difficulty,
            }))
          );
        } else {
          setTechStackWithPercentages([]);
          setTechStackDifficulty([]);
        }
      }
    } catch (error) {
      console.error("Error fetching template details:", error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear timezone error when user selects a timezone
    if (name === "interviewTimezone" && value) {
      setTimezoneError(false);
    }
  };

  // Code Evaluation Handlers
  const handleAddLanguage = () => {
    if (!selectedLanguage) return;

    setFormData((prev) => ({
      ...prev,
      codingLanguages: [
        ...prev.codingLanguages,
        { language: selectedLanguage.toLowerCase(), complexity: selectedComplexity },
      ],
    }));
    setSelectedLanguage("");
    setSelectedComplexity("Intermediate");
  };

  const handleRemoveLanguage = (indexToRemove) => {
    setFormData((prev) => ({
      ...prev,
      codingLanguages: prev.codingLanguages.filter(
        (_, index) => index !== indexToRemove
      ),
    }));
  };



  const handleFileChange = async (type, file) => {
    if (!file) return;

    if (type === "cv") {
      setCvFile(file);
      setFormData((prev) => ({ ...prev, cvFileName: file?.name || "" }));

      // Immediately extract experience from the uploaded CV
      setExperienceLoading(true);
      try {
        const formDataExp = new FormData();
        formDataExp.append("file", file);
        const resp = await authFetch(
          `${process.env.REACT_APP_REST_BASE_URL}/analyse_resume/extract-experience`,
          {
            method: "POST",
            body: formDataExp,
          }
        );
        if (!resp.ok) {
          const err = await resp.json().catch(() => ({}));
          console.error("CV extraction failed", err);
          setFormData((prev) => ({ ...prev, candidateExperience: "" }));

          // Show error notification to user
          const errorMessage = err?.detail || "Failed to process CV file. Please ensure it is a valid PDF.";
          notify("error", errorMessage);
        } else {
          const result = await resp.json();
          setFormData((prev) => ({
            ...prev,
            candidateExperience: result.experience || "",
          }));
          notify("success", "CV processed successfully!");
        }
      } catch (err) {
        console.error("CV extraction error", err);
        setFormData((prev) => ({ ...prev, candidateExperience: "" }));
        notify("error", "Error processing CV. Please try again.");
      } finally {
        setExperienceLoading(false);
      }
    } else if (type === "image") {
      setImageFile(file);
      setFormData((prev) => ({ ...prev, imageFileName: file?.name || "" }));
    }
  };

  const uploadImage = async (interviewId) => {
    if (!imageFile) return null;
    setImageUploading(true);
    try {
      const imgForm = new FormData();
      imgForm.append("file", imageFile);
      imgForm.append("interview_id", interviewId);

      const url = `${process.env.REACT_APP_REST_BASE_URL}/video_proctoring/upload-candidate-photo/`;

      const resp = await authFetch(url, {
        method: "POST",
        body: imgForm,
      });

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        console.error("Image upload failed", err);
        return null;
      } else {
        const data = await resp.json();
        setImageUploadResponse(data);
        return data;
      }
    } catch (err) {
      console.error("Image upload error", err);
      return null;
    } finally {
      setImageUploading(false);
    }
  };

  const { notify } = useNotification();

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prevent multiple submissions
    if (isSubmitting) {
      return;
    }
    setIsSubmitting(true);

    // Validate timezone
    if (!formData.interviewTimezone) {
      setTimezoneError(true);
      setErrorDialogMessage("Please select a timezone for the interview.");
      setErrorDialogOpen(true);
      setIsSubmitting(false);
      return;
    } else {
      setTimezoneError(false);
    }

    // Validate interview time
    const interviewTime = String(formData.interviewTime || "").trim();

    if (!interviewTime) {
      setErrorDialogMessage("Please select an interview time.");
      setErrorDialogOpen(true);
      setIsSubmitting(false);
      return;
    }

    if (!/^(?:[01]\d|2[0-3]):(?:00|15|30|45)$/.test(interviewTime)) {
      setErrorDialogMessage(
        "Please select a valid interview time in HH:mm format."
      );
      setErrorDialogOpen(true);
      setIsSubmitting(false);
      return;
    }
    // Validate Resume Evaluation percentage
    if (
      formData.resumeEvaluation &&
      (!formData.resumeEvaluationTimePercentage ||
        formData.resumeEvaluationTimePercentage <= 0)
    ) {
      setErrorDialogMessage(
        "Resume Evaluation is enabled. Please add a valid evaluation percentage greater than 0."
      );
      setErrorDialogOpen(true);
      setIsSubmitting(false);
      return;
    }

    // Validate Static Questions
    if (
      formData.staticQuestionsEnabled &&
      (!Array.isArray(formData.staticQuestions) ||
        formData.staticQuestions.length === 0)
    ) {
      setErrorDialogMessage(
        "Static Questions are enabled. Please add at least one question."
      );
      setErrorDialogOpen(true);
      setIsSubmitting(false);
      return;
    }

    // Validate Code Evaluation
    if (formData.codeEvaluation) {
      if (
        !Array.isArray(formData.codingLanguages) ||
        formData.codingLanguages.length === 0
      ) {
        setErrorDialogMessage(
          "Code Evaluation is enabled. Please add at least one programming language and level."
        );
        setErrorDialogOpen(true);
        setIsSubmitting(false);
        return;
      }

      const invalidCodingLanguage = formData.codingLanguages.some(
        (item) =>
          !item?.language?.trim() ||
          !item?.complexity?.trim()
      );

      if (invalidCodingLanguage) {
        setErrorDialogMessage(
          "Each coding evaluation must have both a language and level."
        );
        setErrorDialogOpen(true);
        setIsSubmitting(false);
        return;
      }
    }

    // Validate CV is provided when Resume Evaluation is enabled
    if (formData.resumeEvaluation && !cvFile && !formData.cvFileName) {
      setErrorDialogMessage("Please upload a candidate CV. It is required when Resume Evaluation is enabled.");
      setErrorDialogOpen(true);
      setIsSubmitting(false);
      return;
    }

    // Validate photo is provided when Video Proctoring is enabled
    if (videoProctoringEnabled && !imageFile && !formData.imageFileName) {
      setErrorDialogMessage("Please upload a candidate photo. It is required when Video Proctoring is enabled.");
      setErrorDialogOpen(true);
      setIsSubmitting(false);
      return;
    }

    // Validate total percentage (Tech Stack + Resume Evaluation)
    const techTotal = techStackWithPercentages.reduce(
      (sum, t) => sum + (t.percentage || 0),
      0
    );
    const resumeEvalPercent = formData.resumeEvaluationTimePercentage || 0;
    if (techTotal + resumeEvalPercent !== 100) {
      setErrorDialogMessage(
        "Total percentage (Tech Stack + Resume Evaluation) must be exactly 100%."
      );
      setErrorDialogOpen(true);
      setIsSubmitting(false);
      return;
    }

    let experienceToSend = formData.candidateExperience;
    if (cvFile && !experienceToSend) {
      try {
        setExperienceLoading(true);
        const formDataExp = new FormData();
        formDataExp.append("file", cvFile);
        const resp = await authFetch(
          `${process.env.REACT_APP_REST_BASE_URL}/analyse_resume/extract-experience`,
          {
            method: "POST",
            body: formDataExp,
          }
        );
        const result = await resp.json();
        experienceToSend = result.experience || "";
      } catch (err) {
        experienceToSend = "";
      } finally {
        setExperienceLoading(false);
      }
    }

    const formPayload = new FormData();
    formPayload.append("templateId", formData.templateId);
    formPayload.append("candidateName", formData.candidateName);
    formPayload.append("candidateEmail", formData.candidateEmail);
    formPayload.append("interviewDate", formData.interviewDate);
    formPayload.append("interviewTime", formData.interviewTime);
    formPayload.append("interviewTimezone", formData.interviewTimezone);
    const lockedTemplateTechStack = Array.isArray(selectedTemplate?.techStackData)
      ? selectedTemplate.techStackData
      : [];
    const effectiveInterviewDuration = isPlannedReadyInterviewLocked
      ? selectedTemplate?.interviewDuration || formData.interviewDuration
      : formData.interviewDuration;

    formPayload.append("interviewDuration", effectiveInterviewDuration);
    formPayload.append("candidateExperience", experienceToSend);
    formPayload.append("resumeEvaluation", formData.resumeEvaluation || false);
    formPayload.append(
      "resumeEvaluationTimePercentage",
      Number(formData.resumeEvaluationTimePercentage) || 0
    );
    formPayload.append(
      "staticQuestionsEnabled",
      formData.staticQuestionsEnabled || false
    );
    formPayload.append(
      "staticQuestions",
      JSON.stringify(formData.staticQuestions || [])
    );
    formPayload.append("codeEvaluation", formData.codeEvaluation || false);
    formPayload.append("codingLanguage", JSON.stringify(formData.codingLanguages || []));
    formPayload.append(
      "secondCameraEnabled",
      formData.secondCameraEnabled || false
    );
    formPayload.append(
      "systemCheckEnabled",
      formData.systemCheckEnabled || false
    );
    formPayload.append(
      "codeEvaluationEnabled",
      formData.codeEvaluationEnabled || false
    );
    formPayload.append(
      "resumeEvaluationEnabled",
      formData.resumeEvaluationEnabled || false
    );
    formPayload.append(
      "preferredLanguage",
      formData.preferredLanguage || "English"
    );

    const mergedTechStack = techStackWithPercentages.map((tech) => {
      const difficultyObj = techStackDifficulty.find(
        (d) => d.name === tech.name
      ) || { difficulty: "Intermediate" };
      return {
        name: tech.name,
        percentage: tech.percentage,
        difficulty: difficultyObj.difficulty,
      };
    });

    formPayload.append(
      "techStackData",
      JSON.stringify(
        isPlannedReadyInterviewLocked ? lockedTemplateTechStack : mergedTechStack
      )
    );
    if (cvFile) formPayload.append("cv", cvFile);
    if (imageFile) formPayload.append("image", imageFile);

    try {
      let interviewIdResult = null;
      if (isEditMode) {
        // updateInterview may return the updated interview object or nothing
        const updated = await updateInterview(id, formPayload);
        // try to extract interview id from update response. fallback to `id` param.
        interviewIdResult =
          updated?.interviewId || updated?._id || updated?.id || id;
        console.log("Updated interview response:", updated);
        setSuccessDialogMessage("Interview updated successfully!");
        setSuccessDialogOpen(true);
      } else {
        const data = await scheduleInterview(formPayload);
        interviewIdResult = data?.interviewId || data?._id || data?.id;
        console.log("Scheduled interview response:", data);
        setSuccessDialogMessage("Interview scheduled successfully!");
        setSuccessDialogOpen(true);
      }

      // Upload image after creating/updating the interview
      if (imageFile) {
        const uploadResp = await uploadImage(interviewIdResult);
        if (!uploadResp) {
          // You can choose to fail the submit or simply warn the user
          console.warn("Image upload failed or returned no response");
          // optionally: alert('Interview saved but image upload failed.');
        }
      }
    } catch (error) {
      console.error("Error saving interview:", error); // Log the full error for debugging

      let errorMessage =
        "An unexpected error occurred while saving the interview."; // Default message

      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        const status = error.response.status;
        const responseError = error.response.data; // This is the response body

        // Try to get the specific error message from the response
        if (responseError && typeof responseError === "object") {
          if (responseError.detail) {
            // Handle the "No face detected" error or other single message errors
            if (typeof responseError.detail === "string") {
              errorMessage = responseError.detail;
            } else if (Array.isArray(responseError.detail)) {
              // Handle Pydantic validation errors (like "Field required")
              const firstError = responseError.detail[0];
              if (firstError && firstError.msg) {
                // Check if the location indicates the 'cv' field is missing
                if (
                  Array.isArray(firstError.loc) &&
                  firstError.loc.includes("cv")
                ) {
                  errorMessage =
                    "CV file is required to schedule the interview.";
                } else if (
                  Array.isArray(firstError.loc) &&
                  firstError.loc.includes("image")
                ) {
                  errorMessage =
                    "Candidate photo is required to schedule the interview.";
                } else if (
                  Array.isArray(firstError.loc) &&
                  firstError.loc.includes("interviewTimezone")
                ) {
                  // Handle specific timezone validation error from backend if any
                  errorMessage = "A valid timezone is required.";
                } else {
                  // If it's a different field, use a more generic message based on the original msg and loc
                  const fieldName = firstError.loc
                    ? firstError.loc.slice(-1)[0]
                    : "unknown field"; // Get the last element of loc array, e.g., 'cv', 'image', etc.
                  errorMessage = `${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)
                    } is required.`;
                }
              }
            }
          }
        }

        // Provide a more specific generic message based on status code if detail wasn't found
        if (
          errorMessage ===
          "An unexpected error occurred while saving the interview."
        ) {
          switch (status) {
            case 400:
              errorMessage = "Bad Request: The data sent was invalid.";
              break;
            case 422:
              errorMessage =
                "Unprocessable Entity: Please check your input fields.";
              break;
            case 500:
              errorMessage = "Internal Server Error: Please try again later.";
              break;
            default:
              errorMessage = `Request failed with status code ${status}.`;
          }
        }
      } else if (error.request) {
        // The request was made but no response was received
        errorMessage =
          "No response received from the server. Please check your connection.";
      } else {
        // Something happened in setting up the request that triggered an Error
        errorMessage = `Request setup error: ${error.message}`;
      }

      setErrorDialogMessage(errorMessage);
      setErrorDialogOpen(true);
      setIsSubmitting(false);
    }
  };

  const handleRemoveTech = (indexToRemove) => {
    if (isPlannedReadyInterviewLocked) return;

    setTechStackWithPercentages((prev) =>
      prev.filter((_, index) => index !== indexToRemove)
    );

    setTechStackDifficulty((prev) =>
      prev.filter((_, index) => index !== indexToRemove)
    );
  };

  return (
    <Container className="ttf-form ttf-interview" maxWidth={false} sx={{ mt: 4, mb: 4, px: 3 }}>
      {/* Added Header Paper - exactly like TemplateForm */}
      <Paper className="ttf-page-header"
        elevation={1}
        sx={{
          p: { xs: 3, md: 4 },
          mb: 3,
          borderRadius: 2,
          textAlign: "left",
        }}
      >
        <Grid container alignItems="center" spacing={2}>
          <Grid item xs>
            <Typography variant="h4" sx={{ fontWeight: 800 }}>
              {isEditMode ? "Edit Interview" : "Schedule New Interview"}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
              {isEditMode
                ? "Update interview details for the candidate"
                : "Schedule an interview for a candidate to streamline your hiring process"}
            </Typography>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              startIcon={<ArrowBackIcon />}
              component={RouterLink}
              to={backButtonPath}
              sx={{
                borderRadius: 2.5,
                fontWeight: 700,
                textTransform: "none",
              }}
            >
              {backButtonLabel}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Paper className="ttf-form-scroll" sx={{ p: 4, borderRadius: "16px" }} elevation={2}>
        {/* Removed the old inline title Box from here */}

        <Box component="form" onSubmit={handleSubmit}>
          {/* Template Selection Card */}
          <StyledCard className="ttf-card">
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <WorkIcon color="primary" sx={{ mr: 1 }} />
                <SectionTitle className="ttf-section-title" variant="h6" sx={{ fontWeight: 700 }}>
                  Job Opening
                </SectionTitle>
              </Box>

              <Grid container spacing={2} sx={{ mb: 2 }}>
                {/* Req ID Selector */}
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel shrink id="reqid-label">Select by Req ID</InputLabel>
                    <Select MenuProps={{ classes: { paper: "ttf-form-menu" } }}
                      labelId="reqid-label"
                      displayEmpty
                      value={selectedReqId}
                      label="Select by Req ID"
                      onChange={(e) => {
                        const reqId = e.target.value;
                        setSelectedReqId(reqId);
                        if (reqId) {
                          // Find the template that matches this reqId
                          const matched = templates.find((t) => t.reqId === reqId);
                          if (matched) {
                            setFormData((prev) => ({ ...prev, templateId: matched._id }));
                            fetchTemplateDetails(matched._id);
                          }
                        } else {
                          setFormData((prev) => ({ ...prev, templateId: "" }));
                          setSelectedTemplate(null);
                        }
                      }}
                      disabled={Boolean(searchParams.get("templateId") && !id)}
                    >

                      <MenuItem value="" disabled><span className="ttf-select-placeholder">Select by Req ID</span></MenuItem>
                      {[...new Map(
                        templates
                          .filter((t) => t.reqId)
                          .map((t) => [t.reqId, t])
                      ).values()].map((t) => (
                        <MenuItem key={t._id} value={t.reqId}>
                          {t.reqId}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Job Opening Selector */}
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required>
                    <InputLabel shrink id="template-label">Select Job Opening</InputLabel>
                    <Select MenuProps={{ classes: { paper: "ttf-form-menu" } }}
                      labelId="template-label"
                      displayEmpty
                      value={formData.templateId}
                      label="Select Job Template"
                      onChange={(e) => {
                        const templateId = e.target.value;
                        setFormData({ ...formData, templateId });
                        // Sync req ID selector
                        const matched = templates.find((t) => t._id === templateId);
                        setSelectedReqId(matched?.reqId || "");
                        if (templateId) fetchTemplateDetails(templateId);
                        else setSelectedTemplate(null);
                      }}
                      disabled={Boolean(searchParams.get("templateId") && !id)}
                    >
                      <MenuItem value="" disabled><span className="ttf-select-placeholder">Select Job Opening</span></MenuItem>
                      {templates
                        .filter((t) => !selectedReqId || t.reqId === selectedReqId)
                        .map((template) => (
                          <MenuItem key={template._id} value={template._id}>
                            {template.name}
                            {template.reqId && (
                              <Typography
                                component="span"
                                variant="caption"
                                sx={{ ml: 1, color: "text.secondary" }}
                              >
                                ({template.reqId})
                              </Typography>
                            )}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </StyledCard>

          {/* Candidate Information Card - MODIFIED LAYOUT */}
          <StyledCard className="ttf-card">
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <PersonIcon color="primary" sx={{ mr: 1 }} />
                <SectionTitle className="ttf-section-title" variant="h6" sx={{ fontWeight: 700 }}>
                  Candidate Information
                </SectionTitle>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Candidate Name"
                    placeholder="Enter Candidate Name"
                    InputLabelProps={{ shrink: true }}
                    name="candidateName"
                    fullWidth
                    required
                    value={formData.candidateName}
                    onChange={handleInputChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Candidate Email"
                    placeholder="Enter Candidate Email"
                    InputLabelProps={{ shrink: true }}
                    name="candidateEmail"
                    type="email"
                    fullWidth
                    required
                    value={formData.candidateEmail}
                    onChange={handleInputChange}
                    error={
                      formData.candidateEmail &&
                      !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/i.test(
                        formData.candidateEmail
                      )
                    }
                    helperText={
                      formData.candidateEmail &&
                        !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/i.test(
                          formData.candidateEmail
                        )
                        ? "Enter a valid email (e.g., example@gmail.com)"
                        : ""
                    }
                  />
                </Grid>
                {/* Modified Grid Layout for Date, Time, and Timezone */}
                <Grid item xs={12}>
                  <Grid container spacing={2} alignItems="flex-end">
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Interview Date"
                        name="interviewDate"
                        type="date"
                        fullWidth
                        required
                        value={formData.interviewDate}
                        onChange={handleInputChange}
                        InputLabelProps={{ shrink: true }}
                        onFocus={(e) => e.target.showPicker?.()}
                        sx={{
                          "& input": {
                            padding: "12px 14px",
                            fontSize: "0.95rem",
                          },
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "12px",
                            backgroundColor: theme.palette.mode === 'dark' ? 'var(--bg-input)' : "#f9f9f9",
                          },
                          "& .MuiOutlinedInput-root:hover": {
                            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : "#f1f1f1",
                          },
                          "& .MuiOutlinedInput-root.Mui-focused": {
                            boxShadow: "0 0 0 2px rgba(25, 118, 210, 0.2)",
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <TimePicker
                          label="Interview Time"
                          ampm={true}
                          timeSteps={{
                            minutes: 15,
                          }}
                          skipDisabled
                          value={
                            formData.interviewTime
                              ? dayjs(`2025-01-01 ${formData.interviewTime}`)
                              : null
                          }
                          onChange={(newValue) => {
                            setFormData((prev) => ({
                              ...prev,
                              interviewTime: newValue
                                ? newValue.format("HH:mm")
                                : "",
                            }));
                          }}
                          slotProps={{
                            desktopPaper: {
                              sx: {
                                "& .MuiMultiSectionDigitalClockSection-root": {
                                  scrollbarWidth: "none",
                                },

                                "& .MuiMultiSectionDigitalClockSection-root::-webkit-scrollbar": {
                                  display: "none",
                                },
                              },
                            },
                            textField: {
                              fullWidth: true,
                              required: true,
                              InputLabelProps: {
                                shrink: true,
                              },
                              sx: {
                                "& input": {
                                  padding: "12px 14px",
                                  fontSize: "0.95rem",
                                  textAlign: "center",
                                },

                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "12px",
                                  backgroundColor:
                                    theme.palette.mode === "dark"
                                      ? "var(--bg-input)"
                                      : "#f9f9f9",
                                },

                                "& .MuiOutlinedInput-root:hover": {
                                  backgroundColor:
                                    theme.palette.mode === "dark"
                                      ? "rgba(255, 255, 255, 0.05)"
                                      : "#f1f1f1",
                                },

                                "& .MuiOutlinedInput-root.Mui-focused": {
                                  boxShadow: "0 0 0 2px rgba(25, 118, 210, 0.2)",
                                },

                                "& .MuiInputLabel-root": {
                                  color:
                                    theme.palette.mode === "dark"
                                      ? "rgba(255,255,255,0.7)"
                                      : "rgba(0,0,0,0.6)",
                                },

                                "& .MuiSvgIcon-root": {
                                  color:
                                    theme.palette.mode === "dark"
                                      ? "#fff"
                                      : "inherit",
                                },
                              },
                            },
                          }}
                        />
                      </LocalizationProvider>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth required error={timezoneError}>
                        <InputLabel shrink id="timezone-label">Timezone</InputLabel>
                        <Select MenuProps={{ classes: { paper: "ttf-form-menu" } }}
                          labelId="timezone-label"
                          displayEmpty
                          name="interviewTimezone"
                          value={formData.interviewTimezone}
                          label="Timezone"
                          onChange={handleInputChange}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                              py: 1.5,
                            },
                          }}
                        >
                          <MenuItem value="" disabled><span className="ttf-select-placeholder">Select Timezone</span></MenuItem>
                          {allTimezones.yourTimezone && [
                            <ListSubheader
                              sx={{
                                fontWeight: 700,
                                fontSize: "0.95rem",
                                color: "#4B1498",          // Teal
                                backgroundColor: "#FAF8FF",
                                lineHeight: "40px",
                                borderBottom: "1px solid #F0EBFF",
                                position: "sticky",
                                top: 0,
                                zIndex: 1,
                              }}
                            >
                              Your Timezone
                            </ListSubheader>,

                            <MenuItem
                              key={allTimezones.yourTimezone.value}
                              value={allTimezones.yourTimezone.value}
                              sx={{
                                py: 1.2,
                                fontSize: "0.92rem",
                                "&.Mui-selected": {
                                  backgroundColor: "#F0EBFF",
                                  fontWeight: 600,
                                },
                                "&:hover": {
                                  backgroundColor: "#F0EBFF",
                                },
                              }}
                            >
                              {allTimezones.yourTimezone.label}
                            </MenuItem>,
                          ]}

                          {allTimezones.commonTimezones.length > 0 && (
                            <ListSubheader
                              sx={{
                                fontWeight: 700,
                                fontSize: "0.95rem",
                                color: "#4B1498",
                                backgroundColor: "#FAF8FF",
                                lineHeight: "40px",
                                borderBottom: "1px solid #F0EBFF",
                                position: "sticky",
                                top: 0,
                                zIndex: 1,
                              }}
                            >
                              Other Timezones in Your Country
                            </ListSubheader>
                          )}

                          {allTimezones.commonTimezones.map((tz) => (
                            <MenuItem
                              key={tz.value}
                              value={tz.value}
                              sx={{
                                py: 1.2,
                                fontSize: "0.92rem",
                                "&.Mui-selected": {
                                  backgroundColor: "#F0EBFF",
                                  fontWeight: 600,
                                },
                                "&:hover": {
                                  backgroundColor: "#F0EBFF",
                                },
                              }}
                            >
                              {tz.label}
                            </MenuItem>
                          ))}

                          <ListSubheader
                            sx={{
                              fontWeight: 700,
                              fontSize: "0.95rem",
                              color: "#4B1498",
                              backgroundColor: "#FAF8FF",
                              lineHeight: "40px",
                              borderBottom: "1px solid #F0EBFF",
                              position: "sticky",
                              top: 0,
                              zIndex: 1,
                            }}
                          >
                            All Other Timezones
                          </ListSubheader>

                          {allTimezones.otherTimezones.map((tz) => (
                            <MenuItem
                              key={tz.value}
                              value={tz.value}
                              sx={{
                                py: 1.2,
                                fontSize: "0.92rem",
                                "&.Mui-selected": {
                                  backgroundColor: "#F0EBFF",
                                  fontWeight: 600,
                                },
                                "&:hover": {
                                  backgroundColor: "#F0EBFF",
                                },
                              }}
                            >
                              {tz.label}
                            </MenuItem>
                          ))}
                        </Select>
                        {timezoneError && (
                          <FormHelperText sx={{ color: "error.main" }}>
                            Please select a timezone.
                          </FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>

            </CardContent>
          </StyledCard>

          {/* File Uploads Card */}
          <StyledCard className="ttf-card ttf-file-uploads">
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <FolderIcon color="primary" sx={{ mr: 1 }} />
                <SectionTitle className="ttf-section-title" variant="h6" sx={{ fontWeight: 700 }}>
                  File Uploads
                </SectionTitle>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      textAlign: "center",
                      p: 2,
                      border: "2px dashed",
                      borderColor: "grey.300",
                      borderRadius: 2,
                    }}
                  >
                    <RecruitmentAsset name="upload" className="ttf-upload-icon" sx={{ width:36, height:36, mx:"auto", mb:1 }} />
                    <Typography variant="body2" gutterBottom>
                      Upload Candidate CV (PDF / Word){" "}
                      {formData.resumeEvaluation ? (
                        <Typography component="span" variant="caption" color="error.main" fontWeight={600}>
                          *Required
                        </Typography>
                      ) : (
                        <Typography component="span" variant="caption" color="text.secondary">
                          (Optional)
                        </Typography>
                      )}
                    </Typography>
                    <UploadButton
                      variant="outlined"
                      component="label"
                      fullWidth
                    >
                      Select File
                      <VisuallyHiddenInput
                        type="file"
                        accept=".pdf,.doc,.docx"
                        onChange={(e) =>
                          handleFileChange("cv", e.target.files[0])
                        }
                      />
                    </UploadButton>
                    {!formData.resumeEvaluation && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                        Enable Resume Evaluation in settings to make CV required.
                      </Typography>
                    )}
                    {formData.cvFileName && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Selected: {formData.cvFileName}
                        </Typography>
                        {experienceLoading && <LinearProgress className="ttf-brand-progress" sx={{ mt: 1 }} />}
                      </Box>
                    )}
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      textAlign: "center",
                      p: 2,
                      border: "2px dashed",
                      borderColor: "grey.300",
                      borderRadius: 2,
                    }}
                  >
                    {imageFile ? (
                    <Avatar
                      sx={{ width: 40, height: 40, mx: "auto", mb: 1 }}
                      src={imageFile ? URL.createObjectURL(imageFile) : null}
                    >
                      <PersonIcon />
                    </Avatar>
                    ) : <RecruitmentAsset name="upload" className="ttf-upload-icon" sx={{width:36,height:36,mx:"auto",mb:1}} />}
                    <Typography variant="body2" gutterBottom>
                      Upload Candidate Photo (JPG/PNG){" "}
                      {videoProctoringEnabled ? (
                        <Typography component="span" variant="caption" color="error.main" fontWeight={600}>
                          *Required
                        </Typography>
                      ) : (
                        <Typography component="span" variant="caption" color="text.secondary">
                          (Optional)
                        </Typography>
                      )}
                    </Typography>
                    <UploadButton
                      variant="outlined"
                      component="label"
                      fullWidth
                    >
                      Select Image
                      <VisuallyHiddenInput
                        type="file"
                        accept=".jpg,.jpeg,.png"
                        onChange={(e) =>
                          handleFileChange("image", e.target.files[0])
                        }
                      />
                    </UploadButton>
                    {!videoProctoringEnabled && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                        Enable Video Proctoring in settings to make this required.
                      </Typography>
                    )}
                    {formData.imageFileName && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Selected: {formData.imageFileName}
                        </Typography>
                        {imageUploading && <LinearProgress className="ttf-brand-progress" sx={{ mt: 1 }} />}
                      </Box>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </StyledCard>

          {selectedTemplate && (
            <StyledCard className="ttf-card ttf-tech-stack-card">
              <CardContent sx={{ p: 3 }}>
                <Box
                  className="ttf-tech-stack-heading"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 3,
                  }}
                >
                  <Box className="ttf-tech-stack-title" sx={{ display: "flex", alignItems: "center" }}>
                    <RecruitmentAsset name="techStack" className="ttf-tech-stack-section-icon" />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Tech Stack Configuration
                    </Typography>
                  </Box>
                  <Button
                    className="ttf-tech-stack-edit"
                    aria-label="Edit tech stack configuration"
                    variant="contained"
                    size="small"
                    onClick={() => {
                      if (!isPlannedReadyInterviewLocked) {
                        setOpenTechEditor(true);
                      }
                    }}
                    disabled={isPlannedReadyInterviewLocked}
                    sx={{
                      textTransform: "capitalize",
                      fontWeight: 600,
                    }}
                  >
                    Edit
                  </Button>
                </Box>

                {/* Tech Stack List */}
                {techStackWithPercentages.length > 0 ? (
                  <>
                    <Box sx={{ mb: 3 }}>
                      <Box
                        className="ttf-tech-stack-grid"
                        sx={{
                          display: "grid",
                          gridTemplateColumns: {
                            xs: "1fr",
                            md: "1fr 1fr",
                          },
                          gap: 2,
                        }}
                      >
                        {techStackWithPercentages.map((tech, index) => {
                          const difficulty =
                            techStackDifficulty.find(
                              (d) => d.name === tech.name
                            )?.difficulty || "Intermediate";

                          return (
                            <Box
                              className="ttf-tech-stack-item"
                              key={index}
                              sx={{
                                p: 2,
                                backgroundColor: theme.palette.mode === 'dark' ? 'var(--bg-input)' : "#fafafa",
                                border: "1px solid var(--border-color)",
                                borderRadius: "10px",

                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 2,
                              }}
                            >
                              {/* LEFT SIDE */}
                              <Box
                                className="ttf-tech-stack-item-details"
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 2,
                                  flex: 1,
                                }}
                              >
                                {/* Tech Name */}
                                <Typography className="ttf-tech-stack-name" sx={{ fontWeight: 600, width: 140 }}>
                                  {tech.name}
                                </Typography>

                                {/* Difficulty */}
                                <Chip
                                  label={difficulty}
                                  className="ttf-tech-stack-difficulty"
                                  size="small"
                                  sx={{
                                    width: 110,
                                    height: 28,
                                    display: "flex",
                                    justifyContent: "center",
                                    fontWeight: 600,
                                    borderRadius: "4px",
                                    ...difficultyTagStyle(difficulty),
                                  }}
                                />

                                {/* Percentage */}
                                <Chip
                                  label={`${tech.percentage}%`}
                                  aria-label={`${tech.name} allocation: ${tech.percentage}%`}
                                  className="ttf-tech-stack-percentage"
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    width: 70,
                                    height: 28,
                                    display: "flex",
                                    justifyContent: "center",
                                    fontWeight: 600,
                                    fontVariantNumeric: "tabular-nums",
                                  }}
                                />
                              </Box>

                              {/* RIGHT SIDE - DELETE ICON 🔥 */}
                              <IconButton
                                className="ttf-tech-stack-delete"
                                aria-label={`Remove ${tech.name} from tech stack`}
                                size="small"
                                color="error"
                                onClick={() => handleRemoveTech(index)}
                                disabled={isPlannedReadyInterviewLocked}
                                sx={{
                                  border: "1px solid #f44336",
                                  borderRadius: "8px",
                                  width: 36,
                                  height: 36,
                                  "&:hover": {
                                    backgroundColor: "#fdecea",
                                  },
                                }}
                              >
                                <RecruitmentAsset name="remove" />
                              </IconButton>
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>

                    <Box className="ttf-allocation-panel">
                      <Typography className="ttf-allocation-title" variant="subtitle2">
                        Percentage Allocation
                      </Typography>
                      {formData.resumeEvaluation && (
                        <Box className="ttf-allocation-row">
                          <Box className="ttf-allocation-row-heading">
                            <Typography variant="body2">Resume Evaluation</Typography>
                            <Typography variant="body2">{formData.resumeEvaluationTimePercentage || 0}%</Typography>
                          </Box>
                          <LinearProgress
                            className="ttf-brand-progress"
                            variant="determinate"
                            value={Math.min(100, formData.resumeEvaluationTimePercentage || 0)}
                          />
                        </Box>
                      )}
                      <Box className="ttf-allocation-row">
                        <Box className="ttf-allocation-row-heading">
                          <Typography variant="body2">Tech Stack</Typography>
                          <Typography variant="body2">
                            {techStackWithPercentages.reduce((sum, tech) => sum + (tech.percentage || 0), 0)}%
                          </Typography>
                        </Box>
                        <LinearProgress
                          className="ttf-brand-progress"
                          variant="determinate"
                          value={Math.min(100, techStackWithPercentages.reduce((sum, tech) => sum + (tech.percentage || 0), 0))}
                        />
                      </Box>
                      <Box className={`ttf-allocation-total ${(techStackWithPercentages.reduce((sum, tech) => sum + (tech.percentage || 0), 0) + (formData.resumeEvaluation ? (formData.resumeEvaluationTimePercentage || 0) : 0)) === 100 ? "is-valid" : "is-invalid"}`}>
                        <Typography variant="body2">Total</Typography>
                        <Typography variant="h6">
                          {techStackWithPercentages.reduce((sum, tech) => sum + (tech.percentage || 0), 0) + (formData.resumeEvaluation ? (formData.resumeEvaluationTimePercentage || 0) : 0)}%
                        </Typography>
                      </Box>
                      {(techStackWithPercentages.reduce((sum, tech) => sum + (tech.percentage || 0), 0) + (formData.resumeEvaluation ? (formData.resumeEvaluationTimePercentage || 0) : 0)) !== 100 && (
                        <Typography className="ttf-allocation-hint" variant="caption">
                          Total percentage must be 100% before scheduling.
                        </Typography>
                      )}
                    </Box>
                  </>
                ) : (
                  <Alert severity="info">
                    No tech stack configured. Click "Edit" to add technologies.
                  </Alert>
                )}
              </CardContent>
            </StyledCard>
          )}

          {/* Interview Details Card - ENHANCED VISUAL LAYOUT */}
          <StyledCard className="ttf-card ttf-interview-details">
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 4 }}>
                <SectionTitle className="ttf-section-title"
                  variant="h6"
                  sx={{ fontWeight: 700, lineHeight: 1.3 }}
                >
                  Interview Details
                </SectionTitle>
              </Box>

              <Grid className="ttf-interview-details-grid" container spacing={2}>
                {/* Duration */}
<Grid className="ttf-details-duration" item xs={12} sm={6}>
  <TextField
    fullWidth
    placeholder="e.g. 30"
    InputLabelProps={{ shrink: true }}
    label={
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.75,
        }}
      >
        <span>Interview Duration (minutes)</span>

        <Box
          component="span"
          sx={{
            color: "#92400e",
            backgroundColor: "#fef3c7",
            px: 0.6,
            py: 0.15,
            borderRadius: "4px",
            fontSize: "0.7rem",
            fontWeight: 500,
            lineHeight: 1.3,
            whiteSpace: "nowrap",
          }}
        >
          Coding evaluations excluded
        </Box>
      </Box>
    }
    type="number"
    required
    value={formData.interviewDuration}
    onChange={(e) => {
      if (isPlannedReadyInterviewLocked) return;

      const val = parseInt(e.target.value, 10);

      if (!isNaN(val) && val > 0) {
        setFormData({
          ...formData,
          interviewDuration: val,
        });
      } else if (e.target.value === "") {
        setFormData({
          ...formData,
          interviewDuration: "",
        });
      }
    }}
    inputProps={{
      min: 1,
      step: 1,
      onWheel: (e) => e.target.blur(),
    }}
    InputProps={{
      readOnly: isPlannedReadyInterviewLocked,
    }}
    sx={{
      "& .MuiOutlinedInput-root": {
        borderRadius: 2,
        py: 1.5,
      },
      "& .MuiInputLabel-root": {
        px: 1,
      },
    }}
  />
</Grid>
                {/* Resume Evaluation */}
                <Grid className="ttf-details-toggle ttf-details-resume" item xs={12} sm={6} md={6}>
                  <FormControl fullWidth>
                    <Box
                      sx={{
                        p: 2,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 2,
                        backgroundColor: theme.palette.mode === 'dark' ? 'var(--bg-input)' : "background.paper",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        minHeight: 60,
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.resumeEvaluation || false}
                            onChange={(e) => {
                              setFormData({
                                ...formData,
                                resumeEvaluation: e.target.checked,
                                resumeEvaluationTimePercentage: e.target.checked
                                  ? formData.resumeEvaluationTimePercentage ||
                                  10
                                  : 0,
                              });
                            }}
                            color="primary"
                            sx={{ mr: 2 }}
                          />
                        }
                        label="Enable Resume Evaluation"
                        sx={{ m: 0, flex: 1 }}
                      />
                    </Box>
                  </FormControl>
                </Grid>

                {/* Static Questions */}
                <Grid className="ttf-details-toggle ttf-details-static" item xs={12} sm={6} md={6}>
                  <FormControl fullWidth>
                    <Box
                      sx={{
                        p: 2,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 2,
                        backgroundColor: theme.palette.mode === 'dark' ? 'var(--bg-input)' : "background.paper",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        minHeight: 60,
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.staticQuestionsEnabled || false}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                staticQuestionsEnabled: e.target.checked,
                              })
                            }
                            color="primary"
                            sx={{ mr: 2 }}
                          />
                        }
                        label="Enable Static Questions"
                        sx={{ m: 0, flex: 1 }}
                      />
                      {formData.staticQuestionsEnabled && (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => setOpenStaticDialog(true)}
                          sx={{
                            borderRadius: 1.5,
                            textTransform: "none",
                            px: 2,
                            py: 0.5,
                            fontSize: "0.875rem",
                          }}
                        >
                          Manage
                        </Button>
                      )}
                    </Box>
                  </FormControl>
                </Grid>

                {/* Second Camera Enabled */}
                <Grid className="ttf-details-toggle ttf-details-camera" item xs={12} sm={6} md={6}>
                  <FormControl fullWidth>
                    <Box
                      sx={{
                        p: 2,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 2,
                        backgroundColor: theme.palette.mode === 'dark' ? 'var(--bg-input)' : "background.paper",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        minHeight: 60,
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.secondCameraEnabled || false}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                secondCameraEnabled: e.target.checked,
                              })
                            }
                            color="primary"
                            sx={{ mr: 2 }}
                          />
                        }
                        label="Enable Second Camera"
                        sx={{ m: 0, flex: 1 }}
                      />
                    </Box>
                  </FormControl>
                </Grid>

                {/* System Check Enabled */}
                <Grid className="ttf-details-toggle ttf-details-system" item xs={12} sm={6} md={6}>
                  <FormControl fullWidth>
                    <Box
                      sx={{
                        p: 2,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 2,
                        backgroundColor: theme.palette.mode === 'dark' ? 'var(--bg-input)' : "background.paper",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        minHeight: 60,
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.systemCheckEnabled || false}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                systemCheckEnabled: e.target.checked,
                              })
                            }
                            color="primary"
                            sx={{ mr: 2 }}
                          />
                        }
                        label="Enable System Check"
                        sx={{ m: 0, flex: 1 }}
                      />
                    </Box>
                  </FormControl>
                </Grid>
                {/* Interview Language */}
                <Grid className="ttf-details-language" item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel shrink required id="interview-preferred-language-label">Interview Language</InputLabel>
                      <Select MenuProps={{ classes: { paper: "ttf-form-menu" } }}
                        labelId="interview-preferred-language-label"
                        displayEmpty
                        label="Interview Language"
                        value={formData.preferredLanguage}
                        onChange={handleInputChange}
                        name="preferredLanguage"
                        disabled={supportedLanguagesLoading}
                        sx={{
                          minWidth: 120,
                          fontSize: "0.95rem",
                          textAlign: "right",
                          "& .MuiSelect-icon": {
                            right: 0,
                          },
                        }}
                      >
                        <MenuItem value="" disabled><span className="ttf-select-placeholder">Select Language</span></MenuItem>
                        {supportedLanguages.map((lang) => (
                          <MenuItem key={lang} value={lang}>
                            {lang}
                          </MenuItem>
                        ))}
                      </Select>
                  </FormControl>
                </Grid>

                {/* Code Evaluation */}
                <Grid className="ttf-details-code" item xs={12} md={12}>
                  <Box
                    className="ttf-code-evaluation-shell"
                    sx={{
                      p: "14px",
                      borderRadius: "7px",
                      border: "1px solid var(--border-color)",
                      backgroundColor: theme.palette.mode === 'dark' ? 'var(--bg-input)' : "background.paper",
                      transition: "all 0.2s ease",
                    }}
                  >
                    <Box className="ttf-code-evaluation-heading" sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.codeEvaluation || false}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                codeEvaluation: e.target.checked,
                                codingLanguages: e.target.checked ? prev.codingLanguages : [],
                              }))
                            }
                            color="primary"
                          />
                        }
                        label="Enable Code Evaluation"
                      />
                    </Box>

                    <Collapse in={formData.codeEvaluation} timeout="auto" unmountOnExit>
                      <Box className="ttf-code-evaluation">
                        <Box className="ttf-code-evaluation-controls">
                          <Box className="ttf-code-evaluation-field">
                            <Typography component="label" className="ttf-code-evaluation-label">Language</Typography>
                            <FormControl fullWidth size="small" disabled={languagesLoading}>
                              <Select MenuProps={{ classes: { paper: "ttf-form-menu" } }}
                                value={selectedLanguage}
                                onChange={(e) => setSelectedLanguage(e.target.value)}
                                inputProps={{ "aria-label": "Language" }}
                                displayEmpty
                                startAdornment={<InputAdornment position="start"><CodeIcon fontSize="inherit" /></InputAdornment>}
                                sx={{ borderRadius: "6px" }}
                              >
                                <MenuItem value="" disabled>Select Language</MenuItem>
                                {languages.map((lang) => (
                                  <MenuItem key={lang} value={lang}>
                                    {lang}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Box>

                          <Box className="ttf-code-evaluation-field">
                            <Typography component="label" className="ttf-code-evaluation-label">Level</Typography>
                            <FormControl fullWidth size="small">
                              <Select MenuProps={{ classes: { paper: "ttf-form-menu" } }}
                                value={selectedComplexity}
                                onChange={(e) => setSelectedComplexity(e.target.value)}
                                inputProps={{ "aria-label": "Level" }}
                                displayEmpty
                                sx={{ borderRadius: "6px" }}
                              >
                                <MenuItem value="" disabled>Select Level</MenuItem>
                                <MenuItem value="Basic">Basic</MenuItem>
                                <MenuItem value="Advanced">Advanced</MenuItem>
                                <MenuItem value="Intermediate">Intermediate</MenuItem>
                              </Select>
                            </FormControl>
                          </Box>

                          <Box className="ttf-code-evaluation-add">
                            <Button
                              variant="contained"
                              disabled={!selectedLanguage}
                              onClick={handleAddLanguage}
                              sx={{
                                height: "40px",
                                textTransform: "none",
                                fontWeight: 600,
                                borderRadius: "8px",
                              }}
                            >
                              Add
                            </Button>
                          </Box>
                        </Box>


                        {/* Selected Languages Display */}
                        {formData.codingLanguages.length > 0 && (
                          <Box className="ttf-code-language-list">
                            <Box className="ttf-code-language-rows" sx={{ display: "flex", flexDirection: "column", gap: 1.2 }}>
                              {formData.codingLanguages.map((item, idx) => (
                                <Box
                                  key={idx}
                                  className="ttf-code-language-row"
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: 2,
                                    px: 1.25,
                                    py: 0.5,
                                    borderRadius: "6px",
                                    border: "1px solid var(--border-color)",
                                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : "#ffffff",
                                    width: "100%",
                                    boxSizing: "border-box",
                                  }}
                                >
                                  {/* Language */}
                                  <Typography
                                    className="ttf-code-language-chip"
                                    sx={{
                                      fontWeight: 600,
                                      fontSize: "0.95rem",
                                      color: "text.primary",
                                      minWidth: 80,
                                    }}
                                  >
                                    {item.language.charAt(0).toUpperCase() + item.language.slice(1)}
                                  </Typography>

                                  {/* Level */}
                                  <Chip
                                    label={
                                      item.complexity.charAt(0).toUpperCase() +
                                      item.complexity.slice(1)
                                    }
                                    size="small"
                                    className="ttf-code-language-difficulty"
                                    sx={{
                                      minWidth: 0,
                                      height: 22,
                                      fontWeight: 600,
                                      borderRadius: "999px",
                                      backgroundColor:
                                        (String(item.complexity || "").toLowerCase() === "advanced" || String(item.complexity || "").toLowerCase() === "high")
                                          ? "#f44336"
                                          : (String(item.complexity || "").toLowerCase() === "intermediate" || String(item.complexity || "").toLowerCase() === "medium")
                                            ? "#ff9800"
                                            : "#4caf50",
                                      color: "#fff",
                                      ...difficultyTagStyle(item.complexity),
                                    }}
                                  />

                                  {/* Delete */}
                                  <IconButton
                                    className="ttf-code-language-delete"
                                    color="error"
                                    size="small"
                                    onClick={() => handleRemoveLanguage(idx)}
                                    sx={{
                                      border: "1px solid #f44336",
                                      borderRadius: "8px",
                                      "&:hover": {
                                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(244, 67, 54, 0.1)' : "#fdecea",
                                      },
                                    }}
                                  >
                                    <RecruitmentAsset name="remove" />
                                  </IconButton>
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        )}
                      </Box>
                    </Collapse>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </StyledCard>
              {/* Submit actions after all interview details */}
              <Box
                sx={{ mt: 3, display: "flex", justifyContent: "flex-end", gap: 2 }}
              >
                <Button
                  variant="outlined"
                  onClick={() => navigate("/interviews")}
                  size="large"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={experienceLoading || imageUploading || isSubmitting}
                  size="large"
                >
                  {experienceLoading
                    ? "Processing CV..."
                    : imageUploading
                      ? "Uploading Image..."
                      : isSubmitting
                        ? "Scheduling..."
                        : isEditMode
                          ? "Update Interview"
                          : "Schedule Interview"}
                </Button>
              </Box>
        </Box>
      </Paper>

      <TechStackModal
        open={openTechEditor}
        onClose={() => setOpenTechEditor(false)}
        techs={techStackWithPercentages}
        difficulties={techStackDifficulty}
        resumeEvaluation={formData.resumeEvaluation}
        resumeEvaluationPercentage={
          formData.resumeEvaluation &&
            formData.resumeEvaluationTimePercentage !== 0
            ? formData.resumeEvaluationTimePercentage
            : ""
        }
        onSave={(updatedTechs, resumeEvalPercent) => {
          if (isPlannedReadyInterviewLocked) return;

          setTechStackWithPercentages(
            updatedTechs.map((t) => ({
              name: t.name,
              percentage: t.percentage,
            }))
          );
          setTechStackDifficulty(
            updatedTechs.map((t) => ({
              name: t.name,
              difficulty: t.difficulty,
            }))
          );
          setFormData((prev) => ({
            ...prev,
            resumeEvaluationTimePercentage: resumeEvalPercent,
          }));
        }}
      />
      {/* Static Questions Dialog */}
      <Dialog className="ttf-dialog ttf-static-dialog"
        open={openStaticDialog}
        onClose={() => setOpenStaticDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          <Box className="ttf-static-dialog-heading">
            <QuizIcon className="ttf-heading-icon" />
            <Box>
              <Typography component="div" className="ttf-static-dialog-title">Manage Static Questions</Typography>
              <Typography variant="body2">Add the questions candidates should answer during the interview.</Typography>
            </Box>
          </Box>
          <IconButton className="ttf-dialog-close" aria-label="Close static questions" onClick={() => setOpenStaticDialog(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
            <TextField
              label="New Question"
              placeholder="Enter question"
              InputLabelProps={{ shrink: true }}
              fullWidth
              value={newQuestion}
              onChange={(e) => setNewQuestion(e.target.value)}
            />
            <Button
              variant="contained"
              onClick={() => {
                if (newQuestion.trim()) {
                  setFormData((prev) => ({
                    ...prev,
                    staticQuestions: [
                      ...prev.staticQuestions,
                      newQuestion.trim(),
                    ],
                  }));
                  setNewQuestion("");
                }
              }}
            >
              Add
            </Button>
          </Box>

          {formData.staticQuestions.length > 0 ? (
            <Box>
              {formData.staticQuestions.map((q, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 1,
                    p: 1,
                    border: "1px solid #ddd",
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="body2">{q}</Typography>
                  <IconButton
                    color="error"
                    size="small"
                    onClick={() => {
                      setFormData((prev) => ({
                        ...prev,
                        staticQuestions: prev.staticQuestions.filter(
                          (_, i) => i !== idx
                        ),
                      }));
                    }}
                  >
                    <RecruitmentAsset name="remove" />
                  </IconButton>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" align="center">
              <img className="ttf-static-empty" src={staticQuestionsIllustration} alt="" />No static questions added yet.
            </Typography>
          )}
        </DialogContent>
      </Dialog>

      {/* Success Dialog */}
      <Dialog className="ttf-dialog"
        open={successDialogOpen}
        onClose={() => {
          setSuccessDialogOpen(false);
          navigate(backButtonPath);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: "#ffffff",
            backdropFilter: "blur(12px)",
            border: "1px solid rgba(75, 20, 152, 0.18)",
            boxShadow: "0 12px 32px rgba(75, 20, 152, 0.16), 0 1px 3px rgba(25, 31, 45, 0.08)",
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1,
            pt: 3,
            px: 3,
            display: "flex",
            alignItems: "center",
            gap: 2,
            background: "#F0EBFF",
          }}
        >
          <CheckCircleIcon
            sx={{
              color: "#4B1498",
              fontSize: 32,
              flexShrink: 0,
            }}
          />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: "#4B1498",
              letterSpacing: "-0.5px",
            }}
          >
            Success!
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ py: 3, px: 3 }}>
          <Typography
            variant="body1"
            sx={{
              color: "text.secondary",
              lineHeight: 1.6,
              fontSize: "0.95rem",
            }}
          >
            {successDialogMessage}
          </Typography>
        </DialogContent>

        <DialogActions
          sx={{
            gap: 1.5,
            p: 3,
            pt: 2,
            borderTop: "1px solid rgba(75, 20, 152, 0.12)",
          }}
        >
          <Button
            onClick={() => {
              setSuccessDialogOpen(false);
              navigate(backButtonPath);
            }}
            variant="contained"
            sx={{
              background: "#4B1498",
              color: "white",
              fontWeight: 600,
              textTransform: "capitalize",
              fontSize: "0.95rem",
              padding: "8px 24px",
              boxShadow: "0 4px 12px rgba(75, 20, 152, 0.24)",
              transition: "all 0.3s ease",
              "&:hover": {
                background: "var(--primary)",
                boxShadow: "0 6px 16px rgba(75, 20, 152, 0.32)",
                transform: "translateY(-2px)",
              },
            }}
          >
            Okay
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error Dialog */}
      <Dialog className="ttf-dialog"
        open={errorDialogOpen}
        onClose={() => setErrorDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: "#ffffff",
            backdropFilter: "blur(12px)",
            border: "1px solid rgba(220, 38, 38, 0.15)",
            boxShadow: "0 8px 32px rgba(220, 38, 38, 0.15), 0 1px 3px rgba(0, 0, 0, 0.08)",
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1,
            pt: 3,
            px: 3,
            display: "flex",
            alignItems: "center",
            gap: 2,
            background: "rgba(220, 38, 38, 0.08)",
          }}
        >
          <ErrorIcon
            sx={{
              color: "#dc2626",
              fontSize: 32,
              flexShrink: 0,
            }}
          />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: "#dc2626",
              letterSpacing: "-0.5px",
            }}
          >
            Oops!
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ py: 3, px: 3 }}>
          <Typography
            variant="body1"
            sx={{
              color: "text.secondary",
              lineHeight: 1.6,
              fontSize: "0.95rem",
            }}
          >
            {errorDialogMessage}
          </Typography>
        </DialogContent>

        <DialogActions
          sx={{
            gap: 1.5,
            p: 3,
            pt: 2,
            borderTop: "1px solid rgba(220, 38, 38, 0.1)",
          }}
        >
          <Button
            onClick={() => setErrorDialogOpen(false)}
            variant="contained"
            sx={{
              background: "#dc2626",
              color: "white",
              fontWeight: 600,
              textTransform: "capitalize",
              fontSize: "0.95rem",
              padding: "8px 24px",
              boxShadow: "0 4px 12px rgba(220, 38, 38, 0.3)",
              transition: "all 0.3s ease",
              "&:hover": {
                background: "#b91c1c",
                boxShadow: "0 6px 16px rgba(220, 38, 38, 0.4)",
                transform: "translateY(-2px)",
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default InterviewForm;
